import type { ReactNode } from 'react';
import { RefreshCw, ScanSearch } from 'lucide-react';
import { BrandIcon, Button, EmptyState, PlatformIcon } from '@/components/ui';
import { useDiscoverHost } from '@/hooks/useNodes';
import { toast } from '@/store/toastStore';
import { errorText } from '@/utils/errors';
import { formatBytes, formatRelative } from '@/utils/format';
import { HOSTED_SITE_KIND_LABELS, label } from '@/utils/labels';
import type { NodeView } from '@/types/api';

/**
 * What an external server's agent found on it (ADR-0033): each nginx site,
 * what it is, how much a copy of it takes. The ones that are watched sites
 * are backed up; the rest are listed so nothing on the server goes unseen.
 * With `watched` — the domains of the panel's sites there — each is marked
 * as in the panel or not (ADR-0058).
 */
export function HostDiscovery({
  node,
  mayManage,
  watched,
}: {
  readonly node: NodeView;
  readonly mayManage: boolean;
  readonly watched?: ReadonlySet<string>;
}): ReactNode {
  const discover = useDiscoverHost({
    onSuccess: () => toast.success('Поиск сайтов запущен'),
    onError: (error) => toast.error('Не удалось запросить', errorText(error)),
  });
  const discovery = node.discovery;

  return (
    <section className="stack stack-tight">
      <div className="row-between">
        <span className="field-label">Что нашёл агент на сервере</span>
        {mayManage && node.agentVersion ? (
          <Button size="sm" variant="ghost" icon={RefreshCw} busy={discover.isPending} onClick={() => discover.mutate(node.id)}>
            Обновить
          </Button>
        ) : null}
      </div>
      {!discovery ? (
        <EmptyState icon={ScanSearch} message={node.agentVersion ? 'Поиск сайтов ещё не выполнялся' : 'Агент не подключён'} />
      ) : (
        <>
          <span className="text-sm muted row-tight row-wrap">
            <span>{discovery.hostname}</span>
            {(discovery.webServers ?? (discovery.nginx ? ['nginx'] : [])).length > 0 ? (
              (discovery.webServers ?? ['nginx']).map((server) => (
                <span key={server} className="platform-name">
                  <BrandIcon name={server === 'apache' ? 'apache' : server === 'docker' ? 'docker' : 'nginx'} size={14} />
                  {server === 'apache' ? 'Apache' : server === 'docker' ? 'Docker' : 'nginx'}
                </span>
              ))
            ) : (
              <span>веб-сервер не найден</span>
            )}
            {node.discoveredAt ? <span>· {formatRelative(node.discoveredAt)}</span> : null}
            {discovery.convertstudio?.console ? <span>· консоль ConvertStudio: {discovery.convertstudio.console}</span> : null}
          </span>
          {discovery.problems.map((problem) => (
            <span key={problem} className="field-error">
              {problem}
            </span>
          ))}
          <ul className="stack stack-tight" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {discovery.sites.map((site) => (
              <li key={`${site.root ?? site.upstream}:${site.names[0]}`} className="stack" style={{ gap: 2 }}>
                <span className="row-tight">
                  <PlatformIcon platform={site.kind} size={14} />
                  <span className="mono">{site.names[0]}</span>
                  {site.names.length > 1 ? <span className="muted text-sm"> +{site.names.length - 1}</span> : null}
                  <span className="muted"> · {label(HOSTED_SITE_KIND_LABELS, site.kind)}</span>
                  {site.version ? <span className="muted"> {site.version}</span> : null}
                  {site.sizeBytes !== null ? <span className="muted"> · {formatBytes(site.sizeBytes)}</span> : null}
                  {watched ? (
                    site.names.some((name) => watched.has(name.replace(/^www\./, ''))) ? (
                      <span className="tag tag-success">в панели</span>
                    ) : (
                      <span className="tag">не в панели</span>
                    )
                  ) : null}
                </span>
                <span className="mono text-sm faint">{site.root ?? site.upstream ?? ''}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
